/**
 * Utility functions for handling filter values and preventing empty filters from being sent to the backend
 */

/**
 * Normalizes a filter value to ensure it's not empty or invalid
 * @param input - The input value to normalize
 * @returns The normalized value or null if empty/invalid
 */
export function normalizeFilterValue(input: any): string | null {
  if (input === null || input === undefined) {
    return null;
  }

  if (Array.isArray(input)) {
    if (input.length === 0) {
      return null;
    }
    const joined = input.join(',');
    return joined && joined.trim().length > 0 ? joined : null;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  // Fallback: coerce to string
  const coerced = String(input);
  return coerced.trim().length > 0 ? coerced : null;
}

/**
 * Builds otherFilters array for CRM mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildCrmOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  const companiesValue = normalizeFilterValue(currentFilters.companies);
  if (companiesValue !== null) {
    otherFilters.push({ name: 'companies', operator: 'in', value: companiesValue });
  }

  const usersValue = normalizeFilterValue(currentFilters.users);
  if (usersValue !== null) {
    otherFilters.push({ name: 'users', operator: 'in', value: usersValue });
  }

  const clientsValue = normalizeFilterValue(currentFilters.clients);
  if (clientsValue !== null) {
    otherFilters.push({ name: 'partners', operator: 'in', value: clientsValue });
  }

  // Boolean filter: withoutBudget
  if (currentFilters.withoutBudget === true) {
    otherFilters.push({ name: 'withoutBudget', operator: 'eq', value: true });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Projects mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildProjectsOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  const projectsValue = normalizeFilterValue(currentFilters.projects);
  if (projectsValue !== null) {
    otherFilters.push({ name: 'projects', operator: 'in', value: projectsValue });
  }

  const merchandiseValue = normalizeFilterValue(currentFilters.merchandise);
  if (merchandiseValue !== null) {
    otherFilters.push({ name: 'commodities', operator: 'in', value: merchandiseValue });
  }

  const statusValue = normalizeFilterValue(currentFilters.status);
  if (statusValue !== null) {
    otherFilters.push({ name: 'status', operator: 'in', value: statusValue });
  }

  const mblValue = normalizeFilterValue(currentFilters.mbl);
  if (mblValue !== null) {
    otherFilters.push({ name: 'mbl', operator: 'in', value: mblValue });
  }

  const hblValue = normalizeFilterValue(currentFilters.hbl);
  if (hblValue !== null) {
    otherFilters.push({ name: 'hbl', operator: 'in', value: hblValue });
  }

  if (currentFilters.inExecution === true) {
    otherFilters.push({ name: 'inExecution', operator: 'eq', value: true });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Inventory Operations mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildInventoryOperationsOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  const originValue = normalizeFilterValue(currentFilters.origin);
  if (originValue !== null) {
    otherFilters.push({ name: 'location_id', operator: 'in', value: originValue });
  }

  const destinationValue = normalizeFilterValue(currentFilters.destination);
  if (destinationValue !== null) {
    otherFilters.push({ name: 'location_dest_id', operator: 'in', value: destinationValue });
  }

  const supplierValue = normalizeFilterValue(currentFilters.supplier);
  if (supplierValue !== null) {
    otherFilters.push({ name: 'partner_id', operator: 'in', value: supplierValue });
  }

  const productValue = normalizeFilterValue(currentFilters.product);
  if (productValue !== null) {
    otherFilters.push({ name: 'product_id', operator: 'in', value: productValue });
  }

  const batchValue = normalizeFilterValue(currentFilters.batch);
  if (batchValue !== null) {
    otherFilters.push({ name: 'x_studio_lotes', operator: 'in', value: batchValue });
  }

  const serialNumberValue = normalizeFilterValue(currentFilters.serialNumber);
  if (serialNumberValue !== null) {
    otherFilters.push({ name: 'lot_id', operator: 'in', value: serialNumberValue });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Inventory Stock mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildInventoryStockOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  const productValue = normalizeFilterValue(currentFilters.product);
  if (productValue !== null) {
    otherFilters.push({ name: 'product_id', operator: 'in', value: productValue });
  }

  const locationValue = normalizeFilterValue(currentFilters.location);
  if (locationValue !== null) {
    otherFilters.push({ name: 'location_id', operator: 'in', value: locationValue });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Temperature mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildTemperatureOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  const bessValue = normalizeFilterValue(currentFilters.bess);
  if (bessValue !== null) {
    otherFilters.push({ name: 'bess', operator: 'in', value: bessValue });
  }

  const mblValue = normalizeFilterValue(currentFilters.mbl);
  if (mblValue !== null) {
    otherFilters.push({ name: 'mbl', operator: 'in', value: mblValue });
  }

  const destinationTransportRefValue = normalizeFilterValue(currentFilters.destinationTransportRef);
  if (destinationTransportRefValue !== null) {
    otherFilters.push({ name: 'destinationTransportRef', operator: 'in', value: destinationTransportRefValue });
  }

  const projectsValue = normalizeFilterValue(currentFilters.projects);
  if (projectsValue !== null) {
    otherFilters.push({ name: 'projects', operator: 'in', value: projectsValue });
  }

  const trackerValue = normalizeFilterValue(currentFilters.tracker);
  if (trackerValue !== null) {
    otherFilters.push({ name: 'tracker', operator: 'in', value: trackerValue });
  }

  const loteValue = normalizeFilterValue(currentFilters.lote);
  if (loteValue !== null) {
    otherFilters.push({ name: 'lote', operator: 'in', value: loteValue });
  }

  const batchValue = normalizeFilterValue(currentFilters.batch);
  if (batchValue !== null) {
    otherFilters.push({ name: 'x_studio_lotes', operator: 'in', value: batchValue });
  }

  const serialNumberValue = normalizeFilterValue(currentFilters.serialNumber);
  if (serialNumberValue !== null) {
    otherFilters.push({ name: 'lot_id', operator: 'in', value: serialNumberValue });
  }

  // Temperature range filters
  if (currentFilters.temperatureMin !== null && currentFilters.temperatureMin !== undefined) {
    otherFilters.push({ name: 'temperature_min', operator: 'gte', value: currentFilters.temperatureMin });
  }

  if (currentFilters.temperatureMax !== null && currentFilters.temperatureMax !== undefined) {
    otherFilters.push({ name: 'temperature_max', operator: 'lte', value: currentFilters.temperatureMax });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Carbon Footprint mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildCarbonFootprintOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  // Include project filter only if a value is selected
  const projectCarbonValue = normalizeFilterValue(currentFilters.projectCarbon);
  if (projectCarbonValue !== null) {
    otherFilters.push({ name: 'project', operator: 'eq', value: projectCarbonValue });
  }

  const shippingRefValue = normalizeFilterValue(currentFilters.shippingRef);
  if (shippingRefValue !== null) {
    otherFilters.push({ name: 'shipping_ref', operator: 'in', value: shippingRefValue });
  }

  const mblCarbonValue = normalizeFilterValue(currentFilters.mblCarbon);
  if (mblCarbonValue !== null) {
    otherFilters.push({ name: 'mbl', operator: 'in', value: mblCarbonValue });
  }

  const containerValue = normalizeFilterValue(currentFilters.container);
  if (containerValue !== null) {
    otherFilters.push({ name: 'container', operator: 'in', value: containerValue });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Operations mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildOperationsOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  const operationsProjectValue = normalizeFilterValue(currentFilters.operationsProject);
  if (operationsProjectValue !== null) {
    otherFilters.push({ name: 'serviceProject', operator: 'in', value: operationsProjectValue });
  }

  const operationsContainerValue = normalizeFilterValue(currentFilters.operationsContainer);
  if (operationsContainerValue !== null) {
    otherFilters.push({ name: 'containerRef', operator: 'in', value: operationsContainerValue });
  }

  const operationsServicesValue = normalizeFilterValue(currentFilters.operationsServices);
  if (operationsServicesValue !== null) {
    otherFilters.push({ name: 'service', operator: 'in', value: operationsServicesValue });
  }

  const operationsMBLValue = normalizeFilterValue(currentFilters.operationsMBL);
  if (operationsMBLValue !== null) {
    otherFilters.push({ name: 'mbl', operator: 'in', value: operationsMBLValue });
  }

  const operationsHBLValue = normalizeFilterValue(currentFilters.operationsHBL);
  if (operationsHBLValue !== null) {
    otherFilters.push({ name: 'hbl', operator: 'in', value: operationsHBLValue });
  }

  // dateFilterType se envía como campo directo en el body (dateField), no en otherFilters

  return otherFilters;
}

/**
 * Builds otherFilters array for CRM Totals mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildCrmTotalsOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  // Handle company_id filter (backend uses company_id, but we store it as companies in FilterService)
  const companiesValue = normalizeFilterValue(currentFilters.companies);
  if (companiesValue !== null) {
    otherFilters.push({ name: 'company_id', operator: 'in', value: companiesValue });
  }

  // Get the selected filter property
  const filterProperty = currentFilters.filterProperty;

  // Only apply amount range filters if filterProperty is selected
  if (!filterProperty) {
    return otherFilters;
  }

  // Amount range filters - use the selected filter property with gt/lt operators
  // Treat null, undefined, empty string as "not set" - allow 0 as valid value
  // Input can be number, null, undefined, or empty string - normalize to number | null
  const amountMinRaw = currentFilters.amountMin;
  const amountMinValue = (amountMinRaw !== null &&
    amountMinRaw !== undefined &&
    amountMinRaw !== '' &&
    !isNaN(Number(amountMinRaw)))
    ? Number(amountMinRaw)
    : null;

  const amountMaxRaw = currentFilters.amountMax;
  const amountMaxValue = (amountMaxRaw !== null &&
    amountMaxRaw !== undefined &&
    amountMaxRaw !== '' &&
    !isNaN(Number(amountMaxRaw)))
    ? Number(amountMaxRaw)
    : null;

  // Validate that values are valid numbers (allow 0 as valid value)
  // hasMin/hasMax will be true if value is 0 (since 0 !== null and !isNaN(0) is true)
  // hasMin/hasMax will be false only if value is null/undefined/NaN
  const hasMin = amountMinValue !== null && !isNaN(amountMinValue);
  const hasMax = amountMaxValue !== null && !isNaN(amountMaxValue);

  // Only apply filters if at least one amount value is provided
  // The select alone is not sufficient for filtering
  if (!hasMin && !hasMax) {
    return otherFilters;
  }

  // Use selected filter property > amountMin to filter by amount range (lower limit)
  if (hasMin) {
    otherFilters.push({
      name: filterProperty,
      operator: 'gt',
      value: amountMinValue
    });
  }

  // Use selected filter property < amountMax to filter by amount range (upper limit)
  if (hasMax) {
    otherFilters.push({
      name: filterProperty,
      operator: 'lt',
      value: amountMaxValue
    });
  }

  return otherFilters;
}

/**
 * Builds otherFilters array for Finance mode, ensuring no empty filters are included
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
export function buildFinanceOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  // Handle projects filter
  const projectsValue = normalizeFilterValue(currentFilters.projects);
  if (projectsValue !== null) {
    otherFilters.push({ name: 'project_id', operator: 'in', value: projectsValue });
  }

  // Handle country filter
  const countryValue = normalizeFilterValue(currentFilters.country);
  if (countryValue !== null) {
    otherFilters.push({ name: 'country_id', operator: 'in', value: countryValue });
  }

  // Amount range filters - apply to the selected margin field
  const amountMinValue = currentFilters.amountMin !== null && currentFilters.amountMin !== undefined
    ? Number(currentFilters.amountMin)
    : null;
  const amountMaxValue = currentFilters.amountMax !== null && currentFilters.amountMax !== undefined
    ? Number(currentFilters.amountMax)
    : null;

  // Validate that values are valid numbers (allow 0 as valid value)
  const hasMin = amountMinValue !== null && !isNaN(amountMinValue);
  const hasMax = amountMaxValue !== null && !isNaN(amountMaxValue);

  // Debug logging
  console.log('[buildFinanceOtherFilters] Debug:', {
    currentFilters,
    amountMinValue,
    amountMaxValue,
    hasMin,
    hasMax,
    margin: currentFilters.margin
  });

  // Only apply range filter if there's a margin selected AND there are amount values
  // The margin field determines which field to filter by
  if (currentFilters.margin && (hasMin || hasMax)) {
    const marginField = currentFilters.margin; // Use the selected margin field from the select

    if (hasMin) {
      otherFilters.push({
        name: marginField,
        operator: 'gt',
        value: amountMinValue
      });
    }
    if (hasMax) {
      otherFilters.push({
        name: marginField,
        operator: 'lt',
        value: amountMaxValue
      });
    }
  }

  console.log('[buildFinanceOtherFilters] Result otherFilters:', otherFilters);

  return otherFilters;
}

/**
 * Builds otherFilters array based on the report type, automatically detecting which filters to use
 * @param currentFilters - The current filter values
 * @param reportType - The type of report (crm, projects, temperature, inventory, etc.)
 * @returns Array of valid filter objects
 */
export function buildReportOtherFilters(currentFilters: any, reportType?: string): any[] {
  // Si no se especifica el tipo de reporte, intentar detectarlo automáticamente
  if (!reportType) {
    reportType = detectReportType(currentFilters);
  }

  // Normalizar el tipo de reporte para manejar diferentes formatos
  const normalizedType = normalizeReportType(reportType);

  switch (normalizedType) {
    case 'crm':
      return buildCrmOtherFilters(currentFilters);
    case 'crm_totals':
    case 'crm-totals':
      return buildCrmTotalsOtherFilters(currentFilters);
    case 'finance':
      return buildFinanceOtherFilters(currentFilters);
    case 'projects':
      return buildProjectsOtherFilters(currentFilters);
    case 'temperature':
      return buildTemperatureOtherFilters(currentFilters);
    case 'inventory_operations':
      return buildInventoryOperationsOtherFilters(currentFilters);
    case 'inventory_stock':
      return buildInventoryStockOtherFilters(currentFilters);
    case 'carbon_footprint':
      return buildCarbonFootprintOtherFilters(currentFilters);
    case 'operations':
      return buildOperationsOtherFilters(currentFilters);
    default:
      // Fallback: usar filtros universales que incluyen todos los filtros disponibles
      return buildUniversalOtherFilters(currentFilters);
  }
}

/**
 * Normalizes report type to handle different naming conventions
 * @param reportType - The report type to normalize
 * @returns Normalized report type
 */
function normalizeReportType(reportType: string): string {
  if (!reportType) return 'crm';

  const type = reportType.toLowerCase();

  // Mapear diferentes formatos al tipo estándar
  if (type === 'inventory-operations' || type === 'inventory_operations') {
    return 'inventory_operations';
  }

  if (type === 'inventory-stock' || type === 'inventory-stocks' || type === 'inventory_stock') {
    return 'inventory_stock';
  }

  if (type === 'carbonfootprint' || type === 'carbon_footprint' || type === 'route-emissions') {
    return 'carbon_footprint';
  }

  if (type === 'batteries-temperature') {
    return 'temperature';
  }

  if (type === 'operations' || type === 'operations-operations') {
    return 'operations';
  }

  if (type === 'crm-crm') {
    return 'crm';
  }

  if (type === 'crm-totals' || type === 'crm_totals') {
    return 'crm_totals';
  }

  if (type === 'finance' || type === 'finance-projects' || type === 'finance/projects') {
    return 'finance';
  }

  if (type === 'projects-projects') {
    return 'projects';
  }

  return type;
}

/**
 * Detects the report type based on the available filters
 * @param currentFilters - The current filter values
 * @returns The detected report type
 */
function detectReportType(currentFilters: any): string {
  // Detectar por filtros específicos de cada reporte
  if (currentFilters.bess || currentFilters.tracker || currentFilters.lote ||
    currentFilters.temperatureMin !== undefined || currentFilters.temperatureMax !== undefined) {
    return 'temperature';
  }

  if (currentFilters.origin || currentFilters.destination || currentFilters.supplier ||
    currentFilters.product || currentFilters.batch || currentFilters.serialNumber) {
    return 'inventory';
  }

  if (currentFilters.projects || currentFilters.merchandise || currentFilters.status ||
    currentFilters.mbl || currentFilters.hbl) {
    return 'projects';
  }

  if (currentFilters.shippingRef || currentFilters.mblCarbon || currentFilters.container) {
    return 'carbon_footprint';
  }

  if (currentFilters.operationsProject || currentFilters.operationsContainer || currentFilters.operationsServices ||
    currentFilters.operationsMBL || currentFilters.operationsHBL) {
    return 'operations';
  }

  // Por defecto, usar CRM
  return 'crm';
}

/**
 * Builds universal otherFilters array that includes all possible filters
 * @param currentFilters - The current filter values
 * @returns Array of valid filter objects
 */
function buildUniversalOtherFilters(currentFilters: any): any[] {
  const otherFilters: any[] = [];

  // Filtros comunes a todos los reportes
  const companiesValue = normalizeFilterValue(currentFilters.companies);
  if (companiesValue !== null) {
    otherFilters.push({ name: 'companies', operator: 'in', value: companiesValue });
  }

  const usersValue = normalizeFilterValue(currentFilters.users);
  if (usersValue !== null) {
    otherFilters.push({ name: 'users', operator: 'in', value: usersValue });
  }

  const clientsValue = normalizeFilterValue(currentFilters.clients);
  if (clientsValue !== null) {
    otherFilters.push({ name: 'partners', operator: 'in', value: clientsValue });
  }

  // Filtros específicos de proyectos
  const projectsValue = normalizeFilterValue(currentFilters.projects);
  if (projectsValue !== null) {
    otherFilters.push({ name: 'projects', operator: 'in', value: projectsValue });
  }

  const merchandiseValue = normalizeFilterValue(currentFilters.merchandise);
  if (merchandiseValue !== null) {
    otherFilters.push({ name: 'commodities', operator: 'in', value: merchandiseValue });
  }

  const statusValue = normalizeFilterValue(currentFilters.status);
  if (statusValue !== null) {
    otherFilters.push({ name: 'status', operator: 'in', value: statusValue });
  }

  const mblValue = normalizeFilterValue(currentFilters.mbl);
  if (mblValue !== null) {
    otherFilters.push({ name: 'mbl', operator: 'in', value: mblValue });
  }

  const hblValue = normalizeFilterValue(currentFilters.hbl);
  if (hblValue !== null) {
    otherFilters.push({ name: 'hbl', operator: 'in', value: hblValue });
  }

  const destinationTransportRefUniversalValue = normalizeFilterValue(currentFilters.destinationTransportRef);
  if (destinationTransportRefUniversalValue !== null) {
    otherFilters.push({ name: 'destinationTransportRef', operator: 'in', value: destinationTransportRefUniversalValue });
  }

  // Filtros específicos de inventario
  const originValue = normalizeFilterValue(currentFilters.origin);
  if (originValue !== null) {
    otherFilters.push({ name: 'location_id', operator: 'in', value: originValue });
  }

  const destinationValue = normalizeFilterValue(currentFilters.destination);
  if (destinationValue !== null) {
    otherFilters.push({ name: 'location_dest_id', operator: 'in', value: destinationValue });
  }

  const supplierValue = normalizeFilterValue(currentFilters.supplier);
  if (supplierValue !== null) {
    otherFilters.push({ name: 'partner_id', operator: 'in', value: supplierValue });
  }

  const productValue = normalizeFilterValue(currentFilters.product);
  if (productValue !== null) {
    otherFilters.push({ name: 'product_id', operator: 'in', value: productValue });
  }

  const batchValue = normalizeFilterValue(currentFilters.batch);
  if (batchValue !== null) {
    otherFilters.push({ name: 'x_studio_lotes', operator: 'in', value: batchValue });
  }

  const serialNumberValue = normalizeFilterValue(currentFilters.serialNumber);
  if (serialNumberValue !== null) {
    otherFilters.push({ name: 'lot_id', operator: 'in', value: serialNumberValue });
  }

  const locationValue = normalizeFilterValue(currentFilters.location);
  if (locationValue !== null) {
    otherFilters.push({ name: 'location_id', operator: 'in', value: locationValue });
  }

  // Filtros específicos de temperatura
  const bessValue = normalizeFilterValue(currentFilters.bess);
  if (bessValue !== null) {
    otherFilters.push({ name: 'bess', operator: 'in', value: bessValue });
  }

  const trackerValue = normalizeFilterValue(currentFilters.tracker);
  if (trackerValue !== null) {
    otherFilters.push({ name: 'tracker', operator: 'in', value: trackerValue });
  }

  const loteValue = normalizeFilterValue(currentFilters.lote);
  if (loteValue !== null) {
    otherFilters.push({ name: 'lote', operator: 'in', value: loteValue });
  }

  // Filtros de rango de temperatura
  if (currentFilters.temperatureMin !== null && currentFilters.temperatureMin !== undefined) {
    otherFilters.push({ name: 'temperature_min', operator: 'gte', value: currentFilters.temperatureMin });
  }

  if (currentFilters.temperatureMax !== null && currentFilters.temperatureMax !== undefined) {
    otherFilters.push({ name: 'temperature_max', operator: 'lte', value: currentFilters.temperatureMax });
  }

  // Filtros específicos de huella de carbono
  const shippingRefValue = normalizeFilterValue(currentFilters.shippingRef);
  if (shippingRefValue !== null) {
    otherFilters.push({ name: 'shipping_ref', operator: 'in', value: shippingRefValue });
  }

  const mblCarbonValue = normalizeFilterValue(currentFilters.mblCarbon);
  if (mblCarbonValue !== null) {
    otherFilters.push({ name: 'mbl', operator: 'in', value: mblCarbonValue });
  }

  const containerValue = normalizeFilterValue(currentFilters.container);
  if (containerValue !== null) {
    otherFilters.push({ name: 'container', operator: 'in', value: containerValue });
  }

  const projectCarbonValue = normalizeFilterValue(currentFilters.projectCarbon || 'project_a');
  if (projectCarbonValue !== null) {
    otherFilters.push({ name: 'projects', operator: 'in', value: projectCarbonValue });
  }

  return otherFilters;
}
